import React, { useState } from 'react';
import { Badge, Spinner, Sym } from '../../../components/app2';
import ReceiptRow from './ReceiptRow';

/**
 * Un periodo de nómina: la fila que se abre.
 *
 * Los recibos del periodo se piden SOLO al abrirlo (una empresa con años de
 * historia tiene decenas de miles; traerlos todos de entrada haría inusable la
 * pantalla). Una vez cargados se quedan en memoria: volver a abrir es inmediato.
 */

const money = (n) => new Intl.NumberFormat('es-MX', {
  style: 'currency', currency: 'MXN', maximumFractionDigits: 0,
}).format(Number(n) || 0);

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** "01–15 jul 2026" · si cruza de mes, "28 jun – 05 jul 2026". */
const etiquetaPeriodo = (inicio, fin) => {
  if (!inicio || !fin) return 'Periodo sin fecha';
  const [ay, am, ad] = String(inicio).slice(0, 10).split('-').map(Number);
  const [by, bm, bd] = String(fin).slice(0, 10).split('-').map(Number);

  if (ay === by && am === bm) {
    return `${String(ad).padStart(2, '0')}–${String(bd).padStart(2, '0')} ${MESES[am - 1]} ${ay}`;
  }
  if (ay === by) {
    return `${String(ad).padStart(2, '0')} ${MESES[am - 1]} – ${String(bd).padStart(2, '0')} ${MESES[bm - 1]} ${ay}`;
  }
  return `${String(ad).padStart(2, '0')} ${MESES[am - 1]} ${ay} – ${String(bd).padStart(2, '0')} ${MESES[bm - 1]} ${by}`;
};

const PeriodRow = ({ periodo, cargarRecibos }) => {
  const [abierto, setAbierto] = useState(false);
  const [recibos, setRecibos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  const etiqueta = etiquetaPeriodo(periodo?.periodStart, periodo?.periodEnd);

  const alternar = async () => {
    if (abierto) { setAbierto(false); return; }
    setAbierto(true);
    if (recibos) return; // ya estaban cargados

    try {
      setCargando(true);
      setError(null);
      setRecibos(await cargarRecibos(periodo));
    } catch (e) {
      console.error('Error cargando los recibos del periodo:', e);
      setError('No se pudieron cargar los recibos de este periodo.');
    } finally {
      setCargando(false);
    }
  };

  return (
    <div
      className="a2-card"
      style={{ overflow: 'hidden', borderColor: abierto ? 'var(--brand-line)' : undefined }}
    >
      <button
        type="button"
        onClick={alternar}
        aria-expanded={abierto}
        aria-label={`${abierto ? 'Contraer' : 'Expandir'} periodo ${etiqueta}`}
        style={{
          display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left',
          padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer',
        }}
      >
        <span
          style={{
            width: 38, height: 38, borderRadius: 10, flexShrink: 0, display: 'grid',
            placeItems: 'center', background: 'var(--brand-soft)',
            border: '1px solid var(--brand-line)', color: 'var(--brand-2)',
          }}
        >
          <Sym name="event_note" size={20} />
        </span>

        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', font: "700 1rem 'Hanken Grotesk'", color: 'var(--heading)' }}>
            {etiqueta}
          </span>
          <span className="a2-tcell-muted" style={{ display: 'block' }}>
            {periodo?.receipts} recibo{periodo?.receipts === 1 ? '' : 's'}
            {periodo?.employees ? ` · ${periodo.employees} colaborador${periodo.employees === 1 ? '' : 'es'}` : ''}
          </span>
        </span>

        <Badge tone="ok" className="a2-hide-sm">{money(periodo?.netAmount)}</Badge>

        <Sym name={abierto ? 'expand_less' : 'expand_more'} size={22} style={{ color: 'var(--muted)', flexShrink: 0 }} />
      </button>

      {abierto ? (
        <div style={{ borderTop: '1px solid var(--line)', padding: '10px 12px 12px' }}>
          {cargando ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 6px', color: 'var(--muted)' }}>
              <Spinner size={17} />
              <span style={{ font: "500 .88rem 'Hanken Grotesk'" }}>Cargando los recibos del periodo…</span>
            </div>
          ) : error ? (
            <p style={{ font: "500 .88rem 'Hanken Grotesk'", color: 'var(--danger)', margin: '10px 6px' }}>
              {error}
            </p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 4, maxHeight: '60vh', overflowY: 'auto' }}>
              {(recibos || []).map((r) => <ReceiptRow key={r.id} receipt={r} />)}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default PeriodRow;
